import { useState, useRef, useEffect, useCallback } from 'react'
import { useSupplierClinicStats } from '@/hooks/useSupplierClinicStats'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { CalendarIcon, Download, Loader2, Search, ChevronsUpDown, Check } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  subMonths,
  subDays,
  differenceInCalendarDays,
} from 'date-fns'
import { zhTW } from 'date-fns/locale'
import { cn } from '@/lib/utils'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import { useViewMode } from '@/stores/viewMode'
import { useAdPlayDetailRecords, AdPlayDetailRecord } from '@/hooks/useAdPlayDetailRecords'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationEllipsis,
} from '@/components/ui/pagination'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useSupplierTrendStats } from '@/hooks/useSupplierTrendStats'
import { useSupplierDailyAdPlayDetail } from '@/hooks/useSupplierDailyAdPlayDetail'
import { useSupplierClinics, type SupplierClinic } from '@/hooks/useSupplierClinics'
import { apiClient } from '@/libs/api_client'
import { useAuthStore } from '@/stores/auth'
import { toPng } from 'html-to-image'

function formatAdDateRange(startDate: string | null, endDate: string | null): string {
  if (!startDate) return '請填入起迄日'
  const start = startDate.replace(/-/g, '/')
  if (!endDate) {
    const days = differenceInCalendarDays(new Date(), new Date(startDate)) + 1
    return `${start} ~ 進行中 (${days}天)`
  }
  const end = endDate.replace(/-/g, '/')
  const days = differenceInCalendarDays(new Date(endDate), new Date(startDate)) + 1
  return `${start} ~ ${end} (${days}天)`
}

function calcAdTotalDays(startDate: string | null, endDate: string | null): string {
  if (!startDate) return ''
  if (!endDate) {
    const days = differenceInCalendarDays(new Date(), new Date(startDate)) + 1
    return `${days}`
  }
  const days = differenceInCalendarDays(new Date(endDate), new Date(startDate)) + 1
  return `${days}`
}

interface DateRange {
  from: Date | undefined
  to: Date | undefined
}

type DatePreset = 'today' | 'yesterday' | 'this-week' | 'last-week' | 'this-month' | 'last-month' | 'custom'

const DATE_PRESETS: { value: DatePreset; label: string }[] = [
  { value: 'today', label: '今日' },
  { value: 'yesterday', label: '昨日' },
  { value: 'this-week', label: '本週' },
  { value: 'last-week', label: '上週' },
  { value: 'this-month', label: '本月' },
  { value: 'last-month', label: '上月' },
  { value: 'custom', label: '自訂' },
]

function getDateRangeFromPreset(preset: DatePreset): DateRange | null {
  const now = new Date()
  const weekOptions = { weekStartsOn: 1 as const }
  switch (preset) {
    case 'today':
      return { from: now, to: now }
    case 'yesterday': {
      const yesterday = subDays(now, 1)
      return { from: yesterday, to: yesterday }
    }
    case 'this-week':
      return { from: startOfWeek(now, weekOptions), to: endOfWeek(now, weekOptions) }
    case 'last-week': {
      const lastWeek = subDays(now, 7)
      return { from: startOfWeek(lastWeek, weekOptions), to: endOfWeek(lastWeek, weekOptions) }
    }
    case 'this-month':
      return { from: startOfMonth(now), to: endOfMonth(now) }
    case 'last-month': {
      const lastMonth = subMonths(now, 1)
      return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) }
    }
    case 'custom':
      return null
  }
}

export default function SupplierReportsPage() {
  const { id: supplierId } = useViewMode()
  const [datePreset, setDatePreset] = useState<DatePreset>('this-month')
  const [dateRange, setDateRange] = useState<DateRange>({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date()),
  })

  // 單一廣告報表狀態
  const [selectedAd, setSelectedAd] = useState<{ adId: number; adTitle: string } | null>(null)
  const [adSearchText, setAdSearchText] = useState('')
  const [adDropdownOpen, setAdDropdownOpen] = useState(false)
  const adDropdownRef = useRef<HTMLDivElement>(null)

  const { stats, clinics, isLoading, isError } = useSupplierClinicStats({
    supplierId: supplierId ?? 0,
    startDate: dateRange.from,
    endDate: dateRange.to,
  })

  // 單一廣告報表 hook
  const {
    stats: singleAdStats,
    isLoading: isLoadingSingleAd,
  } = useSupplierClinicStats({
    supplierId: supplierId ?? 0,
    startDate: dateRange.from,
    endDate: dateRange.to,
    adId: selectedAd?.adId,
  })

  // 關閉下拉選單（點擊外部）
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (adDropdownRef.current && !adDropdownRef.current.contains(event.target as Node)) {
        setAdDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // 過濾廣告清單
  const filteredAds = stats?.adStats.filter((ad) =>
    ad.adTitle.toLowerCase().includes(adSearchText.toLowerCase()),
  ) ?? []

  const exportToExcel = () => {
    if (!stats) return

    const wb = XLSX.utils.book_new()

    // Summary sheet
    const summaryData = [
      ['公播系統報表'],
      [
        `日期區間：${dateRange.from ? format(dateRange.from, 'yyyy/MM/dd') : ''} - ${dateRange.to ? format(dateRange.to, 'yyyy/MM/dd') : ''}`,
      ],
      [],
      ['總覽統計表'],
      ['總播放次數', '總人次數', '診所數量'],
      [stats.summary.totalPlays, stats.summary.totalPeople, stats.summary.clinicCount],
    ]
    const summaryWs = XLSX.utils.aoa_to_sheet(summaryData)
    XLSX.utils.book_append_sheet(wb, summaryWs, '總覽統計')

    // Ad stats sheet
    const adData = [
      ['廣告詳細統計表'],
      ['廣告名稱', '廣告起迄日', '總計天數', '播放次數', '播放裝置數量'],
      ...stats.adStats.map((ad) => [
        ad.adTitle,
        formatAdDateRange(ad.startDate, ad.endDate),
        calcAdTotalDays(ad.startDate, ad.endDate),
        ad.playCount,
        ad.deviceCount,
      ]),
    ]
    const adWs = XLSX.utils.aoa_to_sheet(adData)
    XLSX.utils.book_append_sheet(wb, adWs, '廣告統計')

    // Clinic stats sheet
    const clinicData = [
      ['診所詳細統計表'],
      ['診所名稱', '標籤', '播放次數', '人次數'],
      ...stats.clinicStats.map((clinicStat) => {
        const clinicTags =
          clinics.find((c) => c.clinic_id === clinicStat.clinicId)?.tags ?? []
        return [
          clinicStat.clinicName,
          clinicTags.join(', '),
          clinicStat.playCount,
          clinicStat.peopleCount,
        ]
      }),
    ]
    const clinicWs = XLSX.utils.aoa_to_sheet(clinicData)
    XLSX.utils.book_append_sheet(wb, clinicWs, '診所統計')

    // Generate Excel file
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
    const data = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    saveAs(data, `供應商報表_${format(new Date(), 'yyyyMMdd')}.xlsx`)
  }

  const exportSingleAdToExcel = () => {
    if (!singleAdStats || !selectedAd) return

    const wb = XLSX.utils.book_new()

    const summaryData = [
      ['公播系統報表 - 單一廣告'],
      [`廣告名稱：${selectedAd.adTitle}`],
      [
        `日期區間：${dateRange.from ? format(dateRange.from, 'yyyy/MM/dd') : ''} - ${dateRange.to ? format(dateRange.to, 'yyyy/MM/dd') : ''}`,
      ],
      [],
      ['總覽統計表'],
      ['總播放次數', '總人次數', '診所數量'],
      [
        singleAdStats.summary.totalPlays,
        singleAdStats.summary.totalPeople,
        singleAdStats.summary.clinicCount,
      ],
    ]
    const summaryWs = XLSX.utils.aoa_to_sheet(summaryData)
    XLSX.utils.book_append_sheet(wb, summaryWs, '總覽統計')

    const adData = [
      ['廣告詳細統計表'],
      ['廣告名稱', '廣告起迄日', '總計天數', '播放次數', '播放裝置數量'],
      ...singleAdStats.adStats.map((ad) => [
        ad.adTitle,
        formatAdDateRange(ad.startDate, ad.endDate),
        calcAdTotalDays(ad.startDate, ad.endDate),
        ad.playCount,
        ad.deviceCount,
      ]),
    ]
    const adWs = XLSX.utils.aoa_to_sheet(adData)
    XLSX.utils.book_append_sheet(wb, adWs, '廣告統計')

    const clinicData = [
      ['診所詳細統計表'],
      ['診所名稱', '標籤', '播放次數', '人次數'],
      ...singleAdStats.clinicStats.map((clinicStat) => {
        const clinicTags =
          clinics.find((c) => c.clinic_id === clinicStat.clinicId)?.tags ?? []
        return [
          clinicStat.clinicName,
          clinicTags.join(', '),
          clinicStat.playCount,
          clinicStat.peopleCount,
        ]
      }),
    ]
    const clinicWs = XLSX.utils.aoa_to_sheet(clinicData)
    XLSX.utils.book_append_sheet(wb, clinicWs, '診所統計')

    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
    const data = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    saveAs(data, `供應商報表_${selectedAd.adTitle}_${format(new Date(), 'yyyyMMdd')}.xlsx`)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="mr-2 h-8 w-8 animate-spin" />
        <span>載入中...</span>
      </div>
    )
  }

  if (isError || !stats) {
    return (
      <div className="flex items-center justify-center h-96">
        <span className="text-red-500">載入統計資料失敗</span>
      </div>
    )
  }

  // Handle case where stats exists but summary doesn't
  if (!stats.summary) {
    return (
      <div className="flex items-center justify-center h-96">
        <span className="text-red-500">統計資料格式錯誤</span>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">統計報表</h1>
        <div className="flex gap-4">
          <Select
            value={datePreset}
            onValueChange={(value: DatePreset) => {
              setDatePreset(value)
              const range = getDateRangeFromPreset(value)
              if (range) {
                setDateRange(range)
              }
            }}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DATE_PRESETS.map((preset) => (
                <SelectItem key={preset.value} value={preset.value}>
                  {preset.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {datePreset === 'custom' ? (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-[300px] justify-start text-left font-normal',
                    (!dateRange.from || !dateRange.to) && 'text-muted-foreground',
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateRange.from ? (
                    dateRange.to ? (
                      <>
                        {format(dateRange.from, 'yyyy/MM/dd')} -{' '}
                        {format(dateRange.to, 'yyyy/MM/dd')}
                      </>
                    ) : (
                      format(dateRange.from, 'yyyy/MM/dd')
                    )
                  ) : (
                    <span>選擇日期區間</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-popover" align="start">
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={dateRange.from}
                  selected={dateRange}
                  onSelect={(range) => {
                    if (range?.from) {
                      setDateRange({ from: range.from, to: range.to || range.from })
                    } else {
                      setDateRange({ from: undefined, to: undefined })
                    }
                  }}
                  numberOfMonths={2}
                  locale={zhTW}
                />
              </PopoverContent>
            </Popover>
          ) : (
            <span className="flex items-center text-sm text-muted-foreground">
              {dateRange.from && dateRange.to && (
                <>
                  {format(dateRange.from, 'yyyy/MM/dd')} - {format(dateRange.to, 'yyyy/MM/dd')}
                </>
              )}
            </span>
          )}
        </div>
      </div>

      <Tabs defaultValue="statistics" className="space-y-4">
        <TabsList>
          <TabsTrigger value="statistics">統計報表</TabsTrigger>
          <TabsTrigger value="single-ad">單一廣告報表</TabsTrigger>
          <TabsTrigger value="records">廣告播放記錄</TabsTrigger>
          <TabsTrigger value="trend">趨勢圖</TabsTrigger>
        </TabsList>

        <TabsContent value="statistics">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>
                  公播系統報表（日期區間：
                  {dateRange.from ? format(dateRange.from, 'yyyy/MM/dd') : ''} -{' '}
                  {dateRange.to ? format(dateRange.to, 'yyyy/MM/dd') : ''}）
                </CardTitle>
                <Button onClick={exportToExcel} disabled={!stats}>
                  <Download className="mr-2 h-4 w-4" />
                  匯出 Excel
                </Button>
              </div>
            </CardHeader>
            <CardContent>
          <div className="space-y-6">
            {/* Summary Statistics */}
            <div>
              <h3 className="text-lg font-semibold mb-3">總覽統計表</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>總播放次數</TableHead>
                    <TableHead>總人次數</TableHead>
                    <TableHead>診所數量</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell>{stats.summary.totalPlays.toLocaleString()}</TableCell>
                    <TableCell>{stats.summary.totalPeople.toLocaleString()}</TableCell>
                    <TableCell>{stats.summary.clinicCount}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            {/* Ad Statistics */}
            <div>
              <h3 className="text-lg font-semibold mb-3">廣告詳細統計表</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>廣告名稱</TableHead>
                    <TableHead>廣告起迄日</TableHead>
                    <TableHead>播放次數</TableHead>
                    <TableHead>播放裝置數量</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.adStats.map((ad) => (
                    <TableRow key={ad.adId}>
                      <TableCell>{ad.adTitle}</TableCell>
                      <TableCell>{formatAdDateRange(ad.startDate, ad.endDate)}</TableCell>
                      <TableCell>{ad.playCount.toLocaleString()}</TableCell>
                      <TableCell>{ad.deviceCount}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Clinic Statistics */}
            <div>
              <h3 className="text-lg font-semibold mb-3">診所詳細統計表</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>診所名稱</TableHead>
                    <TableHead>標籤</TableHead>
                    <TableHead>播放次數</TableHead>
                    <TableHead>人次數</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* 使用新的供應商關聯診所資料源 */}
                  {clinics.map((clinic) => {
                    // 從統計資料中找到對應的診所統計
                    const clinicStat = stats?.clinicStats.find(
                      (stat) => stat.clinicId === clinic.clinic_id,
                    )
                    // 優先顯示 alias，沒有則顯示 clinic_name
                    const clinicName = clinic.alias || clinic.clinic_name
                    const clinicTags = clinic.tags ?? []
                    return (
                      <TableRow key={clinic.clinic_id}>
                        <TableCell>{clinicName}</TableCell>
                        <TableCell>
                          {clinicTags.length === 0 ? (
                            <span className="text-muted-foreground">-</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {clinicTags.map((tag) => (
                                <Badge key={tag} variant="secondary" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>{clinicStat?.playCount.toLocaleString() || '0'}</TableCell>
                        <TableCell>{clinicStat?.peopleCount.toLocaleString() || '0'}</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Charts Section */}
            <div className="space-y-6">
              {/* Bar Chart - 診所播放次數與人次數對比 */}
              <div>
                <h3 className="text-lg font-semibold mb-3">各診所播放次數與人次數對比</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={stats.clinicStats.map((clinicStat) => {
                      // 找到對應的 clinic 以取得 alias
                      const clinic = clinics.find((c) => c.clinic_id === clinicStat.clinicId)
                      const clinicName = clinic?.alias || clinicStat.clinicName
                      return {
                        name: clinicName,
                        playCount: clinicStat.playCount,
                        peopleCount: clinicStat.peopleCount,
                      }
                    })}
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip
                      formatter={(value: number) => value.toLocaleString()}
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
                    />
                    <Legend />
                    <Bar dataKey="playCount" fill="#3b82f6" name="播放次數" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="peopleCount" fill="#10b981" name="人次數" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Pie Chart - 各診所播放佔比 */}
              <div>
                <h3 className="text-lg font-semibold mb-3">各診所播放次數佔比</h3>
                <ResponsiveContainer width="100%">
                  <PieChart>
                    <Pie
                      data={stats.clinicStats.map((clinicStat) => {
                        // 找到對應的 clinic 以取得 alias
                        const clinic = clinics.find((c) => c.clinic_id === clinicStat.clinicId)
                        const clinicName = clinic?.alias || clinicStat.clinicName
                        return {
                          name: clinicName,
                          value: clinicStat.playCount,
                        }
                      })}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
                        const radius = innerRadius + (outerRadius - innerRadius) * 0.5
                        const x = cx + radius * Math.cos((-midAngle * Math.PI) / 180)
                        const y = cy + radius * Math.sin((-midAngle * Math.PI) / 180)
                        return (
                          <text
                            x={x}
                            y={y}
                            fill="white"
                            textAnchor={x > cx ? 'start' : 'end'}
                            dominantBaseline="central"
                            className="font-medium"
                          >
                            {`${(percent * 100).toFixed(1)}%`}
                          </text>
                        )
                      }}
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {stats.clinicStats.map((_, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            [
                              '#3b82f6',
                              '#10b981',
                              '#f59e0b',
                              '#ef4444',
                              '#8b5cf6',
                              '#ec4899',
                              '#14b8a6',
                              '#f97316',
                            ][index % 8]
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number) => value.toLocaleString()} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="single-ad">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>單一廣告報表</CardTitle>
                <Button onClick={exportSingleAdToExcel} disabled={!selectedAd || !singleAdStats}>
                  <Download className="mr-2 h-4 w-4" />
                  匯出 Excel
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* 廣告選擇下拉選單 */}
              <div className="mb-6">
                <label className="text-sm font-medium mb-2 block">選擇廣告</label>
                <div className="relative w-[400px]" ref={adDropdownRef}>
                  <div
                    className={cn(
                      'flex items-center border rounded-md cursor-pointer',
                      adDropdownOpen && 'ring-2 ring-ring ring-offset-2',
                    )}
                    onClick={() => setAdDropdownOpen(!adDropdownOpen)}
                  >
                    <Search className="ml-3 h-4 w-4 text-muted-foreground shrink-0" />
                    <Input
                      placeholder={selectedAd ? selectedAd.adTitle : '搜尋廣告名稱...'}
                      value={adSearchText}
                      onChange={(e) => {
                        setAdSearchText(e.target.value)
                        setAdDropdownOpen(true)
                      }}
                      onClick={(e) => {
                        e.stopPropagation()
                        setAdDropdownOpen(true)
                      }}
                      className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                    />
                    <ChevronsUpDown className="mr-3 h-4 w-4 text-muted-foreground shrink-0" />
                  </div>
                  {adDropdownOpen && (
                    <div className="absolute z-50 mt-1 w-full bg-popover border rounded-md shadow-md max-h-60 overflow-y-auto">
                      {filteredAds.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-muted-foreground">
                          此日期區間無播放記錄
                        </div>
                      ) : (
                        filteredAds.map((ad) => (
                          <div
                            key={ad.adId}
                            className={cn(
                              'flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-accent',
                              selectedAd?.adId === ad.adId && 'bg-accent',
                            )}
                            onClick={() => {
                              setSelectedAd({ adId: ad.adId, adTitle: ad.adTitle })
                              setAdSearchText('')
                              setAdDropdownOpen(false)
                            }}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                selectedAd?.adId === ad.adId ? 'opacity-100' : 'opacity-0',
                              )}
                            />
                            {ad.adTitle}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* 單一廣告統計內容 */}
              {!selectedAd ? (
                <div className="flex items-center justify-center h-48 text-muted-foreground">
                  請從上方選擇一支廣告以查看統計報表
                </div>
              ) : isLoadingSingleAd ? (
                <div className="flex items-center justify-center h-48">
                  <Loader2 className="mr-2 h-8 w-8 animate-spin" />
                  <span>載入中...</span>
                </div>
              ) : singleAdStats ? (
                <div className="space-y-6">
                  {/* Summary Statistics */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3">總覽統計表</h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>總播放次數</TableHead>
                          <TableHead>總人次數</TableHead>
                          <TableHead>診所數量</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell>
                            {singleAdStats.summary.totalPlays.toLocaleString()}
                          </TableCell>
                          <TableCell>
                            {singleAdStats.summary.totalPeople.toLocaleString()}
                          </TableCell>
                          <TableCell>{singleAdStats.summary.clinicCount}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>

                  {/* Ad Statistics */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3">廣告詳細統計表</h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>廣告名稱</TableHead>
                          <TableHead>廣告起迄日</TableHead>
                          <TableHead>播放次數</TableHead>
                          <TableHead>播放裝置數量</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {singleAdStats.adStats.map((ad) => (
                          <TableRow key={ad.adId}>
                            <TableCell>{ad.adTitle}</TableCell>
                            <TableCell>{formatAdDateRange(ad.startDate, ad.endDate)}</TableCell>
                            <TableCell>{ad.playCount.toLocaleString()}</TableCell>
                            <TableCell>{ad.deviceCount}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Clinic Statistics */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3">診所詳細統計表</h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>診所名稱</TableHead>
                          <TableHead>標籤</TableHead>
                          <TableHead>播放次數</TableHead>
                          <TableHead>人次數</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {singleAdStats.clinicStats.map((clinicStat) => {
                          const clinicTags =
                            clinics.find((c) => c.clinic_id === clinicStat.clinicId)?.tags ?? []
                          return (
                            <TableRow key={clinicStat.clinicId}>
                              <TableCell>{clinicStat.clinicName}</TableCell>
                              <TableCell>
                                {clinicTags.length === 0 ? (
                                  <span className="text-muted-foreground">-</span>
                                ) : (
                                  <div className="flex flex-wrap gap-1">
                                    {clinicTags.map((tag) => (
                                      <Badge key={tag} variant="secondary" className="text-xs">
                                        {tag}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>
                                {clinicStat.playCount.toLocaleString()}
                              </TableCell>
                              <TableCell>
                                {clinicStat.peopleCount.toLocaleString()}
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Charts */}
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-3">各診所播放次數與人次數對比</h3>
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart
                          data={singleAdStats.clinicStats.map((clinicStat) => ({
                            name: clinicStat.clinicName,
                            playCount: clinicStat.playCount,
                            peopleCount: clinicStat.peopleCount,
                          }))}
                          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                          <YAxis />
                          <Tooltip
                            formatter={(value: number) => value.toLocaleString()}
                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
                          />
                          <Legend />
                          <Bar
                            dataKey="playCount"
                            fill="#3b82f6"
                            name="播放次數"
                            radius={[4, 4, 0, 0]}
                          />
                          <Bar
                            dataKey="peopleCount"
                            fill="#10b981"
                            name="人次數"
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-3">各診所播放次數佔比</h3>
                      <ResponsiveContainer width="100%">
                        <PieChart>
                          <Pie
                            data={singleAdStats.clinicStats.map((clinicStat) => ({
                              name: clinicStat.clinicName,
                              value: clinicStat.playCount,
                            }))}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({
                              cx,
                              cy,
                              midAngle,
                              innerRadius,
                              outerRadius,
                              percent,
                            }) => {
                              const radius =
                                innerRadius + (outerRadius - innerRadius) * 0.5
                              const x =
                                cx + radius * Math.cos((-midAngle * Math.PI) / 180)
                              const y =
                                cy + radius * Math.sin((-midAngle * Math.PI) / 180)
                              return (
                                <text
                                  x={x}
                                  y={y}
                                  fill="white"
                                  textAnchor={x > cx ? 'start' : 'end'}
                                  dominantBaseline="central"
                                  className="font-medium"
                                >
                                  {`${(percent * 100).toFixed(1)}%`}
                                </text>
                              )
                            }}
                            outerRadius={120}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {singleAdStats.clinicStats.map((_, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={
                                  [
                                    '#3b82f6',
                                    '#10b981',
                                    '#f59e0b',
                                    '#ef4444',
                                    '#8b5cf6',
                                    '#ec4899',
                                    '#14b8a6',
                                    '#f97316',
                                  ][index % 8]
                                }
                              />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value: number) => value.toLocaleString()} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="records">
          <RecordsTab
            supplierId={supplierId ?? null}
            dateRange={dateRange}
            ads={stats?.adStats ?? []}
            clinics={clinics}
          />
        </TabsContent>

        <TabsContent value="trend">
          <TrendChartTab
            supplierId={supplierId ?? 0}
            dateRange={dateRange}
            ads={stats?.adStats ?? []}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}

function RecordsTab({
  supplierId,
  dateRange,
  ads,
  clinics,
}: {
  supplierId: number | null
  dateRange: { from: Date | undefined; to: Date | undefined }
  ads: Array<{ adId: number; adTitle: string }>
  clinics: SupplierClinic[]
}) {
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedAdId, setSelectedAdId] = useState<number | undefined>(undefined)
  const [selectedClinicId, setSelectedClinicId] = useState<number | undefined>(undefined)
  const [adDropdownOpen, setAdDropdownOpen] = useState(false)
  const [clinicDropdownOpen, setClinicDropdownOpen] = useState(false)
  const [adSearchText, setAdSearchText] = useState('')
  const [clinicSearchText, setClinicSearchText] = useState('')
  const [isExporting, setIsExporting] = useState(false)
  const token = useAuthStore((state) => state.token)
  const adDropdownRef = useRef<HTMLDivElement>(null)
  const clinicDropdownRef = useRef<HTMLDivElement>(null)
  const pageSize = 20

  // 切換 filter 時重置分頁
  useEffect(() => {
    setCurrentPage(1)
  }, [selectedAdId, selectedClinicId])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (adDropdownRef.current && !adDropdownRef.current.contains(event.target as Node)) {
        setAdDropdownOpen(false)
      }
      if (clinicDropdownRef.current && !clinicDropdownRef.current.contains(event.target as Node)) {
        setClinicDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const {
    records: playRecords,
    pagination,
    isLoading: isLoadingRecords,
  } = useAdPlayDetailRecords({
    supplierId: supplierId ?? undefined,
    adId: selectedAdId,
    clinicId: selectedClinicId,
    startDate: dateRange.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined,
    endDate: dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
    page: currentPage,
    pageSize,
  })

  const filteredAds = ads.filter((ad) =>
    ad.adTitle.toLowerCase().includes(adSearchText.toLowerCase()),
  )
  const filteredClinics = clinics.filter((c) => {
    const name = c.alias || c.clinic_name
    const q = clinicSearchText.toLowerCase()
    const tags = (c as { tags?: string[] }).tags ?? []
    return (
      name.toLowerCase().includes(q) ||
      tags.some((t) => t.toLowerCase().includes(q))
    )
  })

  const selectedAdName = selectedAdId
    ? ads.find((a) => a.adId === selectedAdId)?.adTitle || ''
    : '全部廣告'
  const selectedClinicName = selectedClinicId
    ? (() => {
        const c = clinics.find((c) => c.clinic_id === selectedClinicId)
        return c?.alias || c?.clinic_name || ''
      })()
    : '全部診所'

  const handleExportExcel = async () => {
    if (!supplierId) return
    // #292: 至少要選單一廣告或診所才能匯出，避免「全部資料」匯出造成資料過大逾時錯誤
    if (selectedAdId === undefined && selectedClinicId === undefined) return
    setIsExporting(true)
    try {
      const baseParams: Record<string, string | number> = {
        sortBy: 'played_at',
        sortOrder: 'desc',
        supplierId,
      }
      if (selectedAdId !== undefined) baseParams.adId = selectedAdId
      if (selectedClinicId !== undefined) baseParams.clinicId = selectedClinicId
      if (dateRange.from) baseParams.startDate = format(dateRange.from, 'yyyy-MM-dd')
      if (dateRange.to) baseParams.endDate = format(dateRange.to, 'yyyy-MM-dd')

      // #292: 分批抓取，避免單一請求一次撈 100000 筆 + 關聯資料造成序列化/逾時錯誤
      // 每批 2000 筆，逐頁累加直到取完；以 total 與回傳筆數雙重條件結束，並設安全上限避免無限迴圈
      const BATCH_SIZE = 2000
      const MAX_PAGES = 500
      const items: AdPlayDetailRecord[] = []
      let pageNum = 1
      while (pageNum <= MAX_PAGES) {
        const response = await apiClient.get<{
          success: boolean
          data: { items: AdPlayDetailRecord[]; total: number }
        }>('/api/ad-play/records', {
          params: { ...baseParams, page: pageNum, pageSize: BATCH_SIZE },
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
        const batch = response.data?.data?.items ?? []
        const total = response.data?.data?.total ?? items.length + batch.length
        items.push(...batch)
        if (batch.length < BATCH_SIZE || items.length >= total) break
        pageNum++
      }

      const wb = XLSX.utils.book_new()
      const dateRangeText = `${dateRange.from ? format(dateRange.from, 'yyyy/MM/dd') : ''} - ${dateRange.to ? format(dateRange.to, 'yyyy/MM/dd') : ''}`
      const data = [
        ['廣告播放記錄'],
        [`日期區間：${dateRangeText}`],
        [`廣告：${selectedAdName}`],
        [`診所：${selectedClinicName}`],
        [`總計：${items.length} 筆`],
        [],
        ['播放時間', '廣告名稱', '診所名稱', '廣播裝置'],
        ...items.map((record: AdPlayDetailRecord) => {
          const date = new Date(record.played_at)
          const formattedDate = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')} ${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}:${String(date.getUTCSeconds()).padStart(2, '0')}`
          const clinicName = record.broadcast_device?.clinic
            ? record.broadcast_device.clinic.alias || record.broadcast_device.clinic.clinic_name
            : '-'
          return [
            formattedDate,
            record.advertisement?.title || '無標題',
            clinicName,
            record.broadcast_device?.name || '-',
          ]
        }),
      ]
      const ws = XLSX.utils.aoa_to_sheet(data)
      XLSX.utils.book_append_sheet(wb, ws, '廣告播放記錄')

      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
      saveAs(
        new Blob([buf], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        `廣告播放記錄_${format(new Date(), 'yyyyMMdd')}.xlsx`,
      )
    } finally {
      setIsExporting(false)
    }
  }

  // #292: 至少要選單一廣告或診所才允許匯出（都沒選＝全部資料，匯出不合理且會逾時）
  const canExport = selectedAdId !== undefined || selectedClinicId !== undefined

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>廣告播放記錄</CardTitle>
          <div className="flex items-center gap-3">
            {!canExport && (
              <span className="text-xs text-muted-foreground">
                請先選擇單一廣告或診所後才能匯出
              </span>
            )}
            <Button
              onClick={handleExportExcel}
              disabled={isExporting || !supplierId || !canExport}
            >
              {isExporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {isExporting ? '匯出中...' : '匯出 Excel'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filters row */}
        <div className="flex flex-wrap gap-4 mb-6">
          {/* Clinic filter */}
          <div className="relative w-[250px]" ref={clinicDropdownRef}>
            <div
              className={cn(
                'flex items-center border rounded-md cursor-pointer',
                clinicDropdownOpen && 'ring-2 ring-ring ring-offset-2',
              )}
              onClick={() => setClinicDropdownOpen(!clinicDropdownOpen)}
            >
              <Search className="ml-3 h-4 w-4 text-muted-foreground shrink-0" />
              <Input
                placeholder={selectedClinicName}
                value={clinicSearchText}
                onChange={(e) => {
                  setClinicSearchText(e.target.value)
                  setClinicDropdownOpen(true)
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  setClinicDropdownOpen(true)
                }}
                className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
              <ChevronsUpDown className="mr-3 h-4 w-4 text-muted-foreground shrink-0" />
            </div>
            {clinicDropdownOpen && (
              <div className="absolute z-50 mt-1 w-full bg-popover border rounded-md shadow-md max-h-60 overflow-y-auto">
                <div
                  className={cn(
                    'flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-accent',
                    !selectedClinicId && 'bg-accent',
                  )}
                  onClick={() => {
                    setSelectedClinicId(undefined)
                    setClinicSearchText('')
                    setClinicDropdownOpen(false)
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      !selectedClinicId ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  全部診所
                </div>
                {filteredClinics.map((clinic) => (
                  <div
                    key={clinic.clinic_id}
                    className={cn(
                      'flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-accent',
                      selectedClinicId === clinic.clinic_id && 'bg-accent',
                    )}
                    onClick={() => {
                      setSelectedClinicId(clinic.clinic_id)
                      setClinicSearchText('')
                      setClinicDropdownOpen(false)
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        selectedClinicId === clinic.clinic_id ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    {clinic.alias || clinic.clinic_name}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Ad filter */}
          <div className="relative w-[250px]" ref={adDropdownRef}>
            <div
              className={cn(
                'flex items-center border rounded-md cursor-pointer',
                adDropdownOpen && 'ring-2 ring-ring ring-offset-2',
              )}
              onClick={() => setAdDropdownOpen(!adDropdownOpen)}
            >
              <Search className="ml-3 h-4 w-4 text-muted-foreground shrink-0" />
              <Input
                placeholder={selectedAdName}
                value={adSearchText}
                onChange={(e) => {
                  setAdSearchText(e.target.value)
                  setAdDropdownOpen(true)
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  setAdDropdownOpen(true)
                }}
                className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
              <ChevronsUpDown className="mr-3 h-4 w-4 text-muted-foreground shrink-0" />
            </div>
            {adDropdownOpen && (
              <div className="absolute z-50 mt-1 w-full bg-popover border rounded-md shadow-md max-h-60 overflow-y-auto">
                <div
                  className={cn(
                    'flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-accent',
                    !selectedAdId && 'bg-accent',
                  )}
                  onClick={() => {
                    setSelectedAdId(undefined)
                    setAdSearchText('')
                    setAdDropdownOpen(false)
                  }}
                >
                  <Check
                    className={cn('mr-2 h-4 w-4', !selectedAdId ? 'opacity-100' : 'opacity-0')}
                  />
                  全部廣告
                </div>
                {filteredAds.map((ad) => (
                  <div
                    key={ad.adId}
                    className={cn(
                      'flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-accent',
                      selectedAdId === ad.adId && 'bg-accent',
                    )}
                    onClick={() => {
                      setSelectedAdId(ad.adId)
                      setAdSearchText('')
                      setAdDropdownOpen(false)
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        selectedAdId === ad.adId ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    {ad.adTitle}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {isLoadingRecords ? (
          <div className="flex items-center justify-center h-96">
            <Loader2 className="mr-2 h-8 w-8 animate-spin" />
            <span>載入中...</span>
          </div>
        ) : (
          <div className="space-y-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>播放時間</TableHead>
                  <TableHead>廣告名稱</TableHead>
                  <TableHead>診所名稱</TableHead>
                  <TableHead>廣播裝置</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {playRecords.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground">
                      無播放記錄
                    </TableCell>
                  </TableRow>
                ) : (
                  playRecords.map((record) => {
                    const date = new Date(record.played_at)
                    const formattedDate = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')} ${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}:${String(date.getUTCSeconds()).padStart(2, '0')}`
                    const clinicName = record.broadcast_device.clinic
                      ? record.broadcast_device.clinic.alias ||
                        record.broadcast_device.clinic.clinic_name
                      : '-'

                    return (
                      <TableRow key={record.ad_play_record_id}>
                        <TableCell>{formattedDate}</TableCell>
                        <TableCell>{record.advertisement.title || '無標題'}</TableCell>
                        <TableCell>{clinicName}</TableCell>
                        <TableCell>{record.broadcast_device.name}</TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>

            {pagination && pagination.totalPages > 1 && (
              <div className="mt-4">
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="gap-1 pl-2.5"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        <span>上一頁</span>
                      </Button>
                    </PaginationItem>

                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      let pageNum: number
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1
                      } else if (currentPage <= 3) {
                        pageNum = i + 1
                      } else if (currentPage >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i
                      } else {
                        pageNum = currentPage - 2 + i
                      }

                      return (
                        <PaginationItem key={pageNum}>
                          <PaginationLink
                            onClick={() => setCurrentPage(pageNum)}
                            isActive={currentPage === pageNum}
                            className="cursor-pointer"
                          >
                            {pageNum}
                          </PaginationLink>
                        </PaginationItem>
                      )
                    })}

                    {pagination.totalPages > 5 && currentPage < pagination.totalPages - 2 && (
                      <PaginationItem>
                        <PaginationEllipsis />
                      </PaginationItem>
                    )}

                    <PaginationItem>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === pagination.totalPages}
                        className="gap-1 pr-2.5"
                      >
                        <span>下一頁</span>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>

                <div className="text-sm text-gray-500 text-center mt-2">
                  第 {currentPage} 頁，共 {pagination.totalPages} 頁，總計 {pagination.total} 筆記錄
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function TrendChartTab({
  supplierId,
  dateRange,
  ads,
}: {
  supplierId: number
  dateRange: { from: Date | undefined; to: Date | undefined }
  ads: Array<{ adId: number; adTitle: string }>
}) {
  const [granularity, setGranularity] = useState<'daily' | 'weekly' | 'monthly'>('daily')
  const [selectedClinicId, setSelectedClinicId] = useState<number | undefined>(undefined)
  const [selectedAdId, setSelectedAdId] = useState<number | undefined>(undefined)
  const [clinicDropdownOpen, setClinicDropdownOpen] = useState(false)
  const [adFilterDropdownOpen, setAdFilterDropdownOpen] = useState(false)
  const [clinicSearchText, setClinicSearchText] = useState('')
  const [adFilterSearchText, setAdFilterSearchText] = useState('')
  const chartRef = useRef<HTMLDivElement>(null)
  const clinicDropdownRef = useRef<HTMLDivElement>(null)
  const adFilterDropdownRef = useRef<HTMLDivElement>(null)

  const { clinics } = useSupplierClinics({ supplierId })

  const { trendData, isLoading } = useSupplierTrendStats({
    supplierId,
    startDate: dateRange.from,
    endDate: dateRange.to,
    granularity,
    clinicId: selectedClinicId,
    adId: selectedAdId,
  })

  const { detailData, isLoading: isDetailLoading } = useSupplierDailyAdPlayDetail({
    supplierId,
    startDate: dateRange.from,
    endDate: dateRange.to,
    granularity,
    clinicId: selectedClinicId,
    adId: selectedAdId,
  })

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (clinicDropdownRef.current && !clinicDropdownRef.current.contains(event.target as Node)) {
        setClinicDropdownOpen(false)
      }
      if (adFilterDropdownRef.current && !adFilterDropdownRef.current.contains(event.target as Node)) {
        setAdFilterDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const filteredClinics = clinics.filter((c) => {
    const name = c.alias || c.clinic_name
    const q = clinicSearchText.toLowerCase()
    const tags = (c as { tags?: string[] }).tags ?? []
    return (
      name.toLowerCase().includes(q) ||
      tags.some((t) => t.toLowerCase().includes(q))
    )
  })

  const filteredAds = ads.filter((ad) =>
    ad.adTitle.toLowerCase().includes(adFilterSearchText.toLowerCase()),
  )

  const handleExportPng = useCallback(async () => {
    if (!chartRef.current) return
    const dataUrl = await toPng(chartRef.current, { backgroundColor: '#ffffff' })
    const link = document.createElement('a')
    link.download = `趨勢圖_${format(new Date(), 'yyyyMMdd')}.png`
    link.href = dataUrl
    link.click()
  }, [])

  const handleExportDetailExcel = useCallback(() => {
    if (!detailData || detailData.rows.length === 0) return
    const headers = ['日期', ...detailData.ads.map((a) => a.adTitle), '合計']
    const dataRows = detailData.rows.map((row) => [
      row.date,
      ...detailData.ads.map((a) => row.adPlays[String(a.adId)] || 0),
      row.total,
    ])
    // Totals row
    const totals = ['合計']
    for (const ad of detailData.ads) {
      totals.push(
        String(detailData.rows.reduce((sum, r) => sum + (r.adPlays[String(ad.adId)] || 0), 0)),
      )
    }
    totals.push(String(detailData.rows.reduce((sum, r) => sum + r.total, 0)))
    const ws = XLSX.utils.aoa_to_sheet([headers, ...dataRows, totals])
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, '播放明細')
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
    saveAs(
      new Blob([buf], { type: 'application/octet-stream' }),
      `廣告播放明細_${format(new Date(), 'yyyyMMdd')}.xlsx`,
    )
  }, [detailData])

  const selectedClinicName = selectedClinicId
    ? (() => {
        const c = clinics.find((c) => c.clinic_id === selectedClinicId)
        return c?.alias || c?.clinic_name || ''
      })()
    : '全部診所'

  const selectedAdName = selectedAdId
    ? ads.find((a) => a.adId === selectedAdId)?.adTitle || ''
    : '全部廣告'

  return (
    <>
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>趨勢圖</CardTitle>
          <Button onClick={handleExportPng} disabled={trendData.length === 0}>
            <Download className="mr-2 h-4 w-4" />
            匯出 PNG
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filters row */}
        <div className="flex flex-wrap gap-4 mb-6">
          {/* Granularity switcher */}
          <div className="flex items-center gap-1 border rounded-md p-1">
            {(['daily', 'weekly', 'monthly'] as const).map((g) => (
              <Button
                key={g}
                variant={granularity === g ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setGranularity(g)}
              >
                {g === 'daily' ? '日' : g === 'weekly' ? '週' : '月'}
              </Button>
            ))}
          </div>

          {/* Clinic filter */}
          <div className="relative w-[250px]" ref={clinicDropdownRef}>
            <div
              className={cn(
                'flex items-center border rounded-md cursor-pointer',
                clinicDropdownOpen && 'ring-2 ring-ring ring-offset-2',
              )}
              onClick={() => setClinicDropdownOpen(!clinicDropdownOpen)}
            >
              <Search className="ml-3 h-4 w-4 text-muted-foreground shrink-0" />
              <Input
                placeholder={selectedClinicName}
                value={clinicSearchText}
                onChange={(e) => {
                  setClinicSearchText(e.target.value)
                  setClinicDropdownOpen(true)
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  setClinicDropdownOpen(true)
                }}
                className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
              <ChevronsUpDown className="mr-3 h-4 w-4 text-muted-foreground shrink-0" />
            </div>
            {clinicDropdownOpen && (
              <div className="absolute z-50 mt-1 w-full bg-popover border rounded-md shadow-md max-h-60 overflow-y-auto">
                <div
                  className={cn(
                    'flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-accent',
                    !selectedClinicId && 'bg-accent',
                  )}
                  onClick={() => {
                    setSelectedClinicId(undefined)
                    setClinicSearchText('')
                    setClinicDropdownOpen(false)
                  }}
                >
                  <Check
                    className={cn('mr-2 h-4 w-4', !selectedClinicId ? 'opacity-100' : 'opacity-0')}
                  />
                  全部診所
                </div>
                {filteredClinics.map((clinic) => (
                  <div
                    key={clinic.clinic_id}
                    className={cn(
                      'flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-accent',
                      selectedClinicId === clinic.clinic_id && 'bg-accent',
                    )}
                    onClick={() => {
                      setSelectedClinicId(clinic.clinic_id)
                      setClinicSearchText('')
                      setClinicDropdownOpen(false)
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        selectedClinicId === clinic.clinic_id ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    {clinic.alias || clinic.clinic_name}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Ad filter */}
          <div className="relative w-[250px]" ref={adFilterDropdownRef}>
            <div
              className={cn(
                'flex items-center border rounded-md cursor-pointer',
                adFilterDropdownOpen && 'ring-2 ring-ring ring-offset-2',
              )}
              onClick={() => setAdFilterDropdownOpen(!adFilterDropdownOpen)}
            >
              <Search className="ml-3 h-4 w-4 text-muted-foreground shrink-0" />
              <Input
                placeholder={selectedAdName}
                value={adFilterSearchText}
                onChange={(e) => {
                  setAdFilterSearchText(e.target.value)
                  setAdFilterDropdownOpen(true)
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  setAdFilterDropdownOpen(true)
                }}
                className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
              <ChevronsUpDown className="mr-3 h-4 w-4 text-muted-foreground shrink-0" />
            </div>
            {adFilterDropdownOpen && (
              <div className="absolute z-50 mt-1 w-full bg-popover border rounded-md shadow-md max-h-60 overflow-y-auto">
                <div
                  className={cn(
                    'flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-accent',
                    !selectedAdId && 'bg-accent',
                  )}
                  onClick={() => {
                    setSelectedAdId(undefined)
                    setAdFilterSearchText('')
                    setAdFilterDropdownOpen(false)
                  }}
                >
                  <Check
                    className={cn('mr-2 h-4 w-4', !selectedAdId ? 'opacity-100' : 'opacity-0')}
                  />
                  全部廣告
                </div>
                {filteredAds.map((ad) => (
                  <div
                    key={ad.adId}
                    className={cn(
                      'flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-accent',
                      selectedAdId === ad.adId && 'bg-accent',
                    )}
                    onClick={() => {
                      setSelectedAdId(ad.adId)
                      setAdFilterSearchText('')
                      setAdFilterDropdownOpen(false)
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        selectedAdId === ad.adId ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    {ad.adTitle}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Chart */}
        {isLoading ? (
          <div className="flex items-center justify-center h-96">
            <Loader2 className="mr-2 h-8 w-8 animate-spin" />
            <span>載入中...</span>
          </div>
        ) : trendData.length === 0 ? (
          <div className="flex items-center justify-center h-96 text-muted-foreground">
            此條件下無趨勢資料
          </div>
        ) : (
          <div ref={chartRef}>
            <ResponsiveContainer width="100%" height={450}>
              <LineChart data={trendData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  tick={{ fontSize: 12 }}
                />
                <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                <Tooltip
                  formatter={(value: number, name: string) => [
                    value.toLocaleString(),
                    name,
                  ]}
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
                />
                <Legend />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="playCount"
                  name="播放次數"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="peopleCount"
                  name="人流數"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>

    {/* Detail table */}
    <Card className="mt-4">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>播放明細</CardTitle>
          <Button
            onClick={handleExportDetailExcel}
            disabled={!detailData || detailData.rows.length === 0}
          >
            <Download className="mr-2 h-4 w-4" />
            匯出 Excel
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isDetailLoading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="mr-2 h-8 w-8 animate-spin" />
            <span>載入中...</span>
          </div>
        ) : !detailData || detailData.rows.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-muted-foreground">
            此條件下無播放明細
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky left-0 bg-background z-10">日期</TableHead>
                  {detailData.ads.map((ad) => (
                    <TableHead key={ad.adId} className="text-right whitespace-nowrap">
                      {ad.adTitle}
                    </TableHead>
                  ))}
                  <TableHead className="text-right font-bold">合計</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detailData.rows.map((row) => (
                  <TableRow key={row.date}>
                    <TableCell className="sticky left-0 bg-background z-10 whitespace-nowrap">
                      {row.date}
                    </TableCell>
                    {detailData.ads.map((ad) => (
                      <TableCell key={ad.adId} className="text-right">
                        {(row.adPlays[String(ad.adId)] || 0).toLocaleString()}
                      </TableCell>
                    ))}
                    <TableCell className="text-right font-bold">
                      {row.total.toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
                {/* Totals row */}
                <TableRow className="border-t-2 font-bold">
                  <TableCell className="sticky left-0 bg-background z-10">合計</TableCell>
                  {detailData.ads.map((ad) => (
                    <TableCell key={ad.adId} className="text-right">
                      {detailData.rows
                        .reduce((sum, r) => sum + (r.adPlays[String(ad.adId)] || 0), 0)
                        .toLocaleString()}
                    </TableCell>
                  ))}
                  <TableCell className="text-right">
                    {detailData.rows
                      .reduce((sum, r) => sum + r.total, 0)
                      .toLocaleString()}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
    </>
  )
}
